<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <div class="contents">
          <a class="back_link" @click="backToList">
            &lt; {{ $t("tanaoroshiReader.label.backToList") }}
          </a>

          <div class="page_title">{{ $t("tanaoroshiReader.label.title") }}</div>
          <!-- 15秒ごとの取り直しが失敗しても前の表示のままだったので、いつの表示かを出す（ソースレビュー C9） -->
          <div v-if="reloadFailedAt" class="alert mb-2">{{ $t("tanaoroshiReader.message.reloadFailed", { time: reloadFailedAt }) }}</div>

          <div class="summary">
            <span class="summary_item">
              {{ $t("tanaoroshiReader.label.tanaoroshiId") }}
              <b>{{ tanaoroshiId }}</b>
            </span>
            <span class="summary_item">
              {{ $t("tanaoroshiReader.label.siteCode") }}
              <b>{{ siteCode }}</b>
            </span>
            <span class="summary_item">
              {{ $t("tanaoroshiReader.label.status") }}
              <b>{{ statusLabel }}</b>
              <span v-if="isStarted"> ({{ activeDeviceCount }}/{{ deviceCount }})</span>
            </span>
            <span class="summary_item" v-if="noResponseDeviceCount > 0">
              <b class="alert">
                {{ $t("tanaoroshiReader.label.noResponse") }} {{ noResponseDeviceCount }}
              </b>
            </span>
          </div>

          <div class="table_wrap">
            <table>
              <thead>
                <tr>
                  <th v-for="header in headers" :key="header">
                    {{ $t("tanaoroshiReader.label.table." + header) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reader in readerList" :key="reader.htSerial"
                    :class="{ alert_row: isNoResponse(reader) }">
                  <td v-if="hasCol('num')">{{ reader.num }}</td>
                  <td v-if="hasCol('deviceNo')">{{ reader.deviceNo || "-" }}</td>
                  <td v-if="hasCol('htSerial')">{{ reader.htSerial }}</td>
                  <td v-if="hasCol('saved')">{{ saveStateLabel(reader) }}</td>
                  <td v-if="hasCol('liveness')">{{ progressLabel(reader) }}</td>
                  <td v-if="hasCol('lastHeartbeat')">{{ formatDatetime(reader.lastHeartbeat) }}</td>
                  <td v-if="hasCol('handyConnected')">{{ handyLabel(reader) }}</td>
                  <td v-if="hasCol('readerBattery')">{{ batteryLabel(reader) }}</td>
                  <td v-if="hasCol('readCount')">{{ reader.readCount == null ? "-" : reader.readCount }}</td>
                  <td v-if="hasCol('appVersion')">{{ reader.appVersion || "-" }}</td>
                  <td v-if="hasCol('action')">
                    <v-btn
                      v-if="canForceClose(reader)"
                      size="small"
                      variant="outlined"
                      color="red-darken-2"
                      @click="openForceCloseDialog(reader)"
                    >{{ $t("tanaoroshiReader.action.forceClose") }}</v-btn>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr v-if="readerList.length === 0">
                  <td :colspan="headers.length">{{ $t("common.message.noData") }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="btn_wrap">
            <v-btn class="back_btn" @click="backToList">
              {{ $t("common.action.back") }}
            </v-btn>
          </div>
        </div>

        <v-dialog v-model="forceCloseDialog" width="500" :persistent="isForceClosing">
          <v-card>
            <v-card-text class="pa-5 text-subtitle-1 pre_line">
              {{ $t("tanaoroshiReader.message.confirmForceClose", { reader: forceCloseLabel }) }}
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4">
              <v-spacer />
              <v-btn text @click="closeForceCloseDialog" :disabled="isForceClosing">
                {{ $t("common.action.cancel") }}
              </v-btn>
              <v-btn color="red-darken-2" dark :loading="isForceClosing" @click="forceClose">
                {{ $t("common.action.ok") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- 強制終了できなかったとき（参加していない・保存済み・通信の失敗。ソースレビュー C9） -->
        <MessageDialog v-if="errorDialog" :dialog-data="errorDialog" :handle-ok="() => (errorDialog = null)" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script>
import AppWrapperView from "@/components/views/AppWrapperView";
import MessageDialog from "@/components/dialog/MessageDialog";
import api from "@/apis/staff";
import utils from "@/utils";
import Paths from "@/router/indexPaths";

/**
 * 参加リーダー一覧
 *
 * 棚卸一覧の「実施中 (3/5)」から遷移する詳細。
 * どのリーダーがまだ作業中か・応答しているかを確認し、
 * 戻ってこない端末を打ち切って棚卸を前に進める。
 */
export default {
  name: "TanaoroshiReaderPage",
  components: { AppWrapperView, MessageDialog },

  data() {
    return {
      tanaoroshiId: null,
      siteCode: "",
      statusId: null,
      statusLabel: "",
      deviceCount: 0,
      activeDeviceCount: 0,
      noResponseDeviceCount: 0,
      readerList: [],
      timerId: null,
      forceCloseDialog: false,
      forceCloseTarget: null,
      // 確認文に出すリーダーの名前（端末番号があれば端末番号、無ければ端末シリアル）
      forceCloseLabel: "",
      isForceClosing: false,
      // 最後に一覧を取れた時刻（取り直しが失敗したときだけ表示する）
      lastLoadedAt: "",
      reloadFailedAt: "",
      // 強制終了の失敗を知らせるダイアログ { message, errorList }
      errorDialog: null,
    };
  },

  computed: {
    headers() {
      return this.$getTableDef("TanaoroshiReaderPage");
    },
    // 2:実施中
    isStarted() {
      // 実施中・一時中断・終了（送信が終わっていないリーダーを打ち切るため）
      return [2, 3, 4].includes(this.statusId);
    },
  },

  created() {
    this.tanaoroshiId = Number(this.$route.params.id);
    this.search();
    // 棚卸詳細は15秒間隔で取り直す。実施状況はサーバー側で参照時に算出される。
    this.timerId = setInterval(this.search, 15000);
  },

  unmounted() {
    if (this.timerId) clearInterval(this.timerId);
  },

  methods: {
    search() {
      if (!this.tanaoroshiId) return;
      // 打ち切りの確認中に一覧が入れ替わると操作対象がずれるため、その間は取り直さない
      if (this.forceCloseDialog) return;
      api
        .post("/tanaoroshi/readers", { tanaoroshiId: this.tanaoroshiId })
        .then((res) => {
          if (res.data.status !== "0") {
            this.reloadFailedAt = this.lastLoadedAt || "-";
            return;
          }
          this.applyResult(res.data.results);
          this.lastLoadedAt = new Date().toLocaleTimeString("ja-JP");
          this.reloadFailedAt = "";
        })
        .catch(() => {
          this.reloadFailedAt = this.lastLoadedAt || "-";
        });
    },

    applyResult(r) {
      this.siteCode = r.siteCode;
      this.statusId = r.statusId;
      this.statusLabel = r.statusLabel;
      this.deviceCount = r.deviceCount;
      this.activeDeviceCount = r.activeDeviceCount;
      this.noResponseDeviceCount = r.noResponseDeviceCount;
      this.readerList = r.readerList || [];
    },

    // 列設定（plugins/setting/table/<company>.json）に含まれる列だけ描画する。
    // thead は headers を回しているので、tbody も同じ判定に揃えないと列がずれる。
    hasCol(name) {
      return this.headers.includes(name);
    },

    // 完了 / 未 / 強制終了
    saveStateLabel(reader) {
      return this.$t("tanaoroshiReader.label.save." + (reader.saveState || "NOT_SAVED"));
    },

    // 作業を終えたリーダーは応答を求めていないので実施状況を出さない。
    // サーバーは ONLINE / DELAYED / NO_RESPONSE を返す。実施状況には 実施中・遅延・無応答 をそのまま出す
    // （遅延を「実施中」にまとめていたため、遅延が画面に出なかった。CR-002 S-01）
    isFinished(reader) {
      return reader.saveState === "SAVED" || reader.saveState === "FORCE_CLOSED";
    },

    isInProgress(reader) {
      return !this.isFinished(reader) && reader.liveness !== "NO_RESPONSE";
    },

    isNoResponse(reader) {
      return !this.isFinished(reader) && reader.liveness === "NO_RESPONSE";
    },

    progressLabel(reader) {
      if (this.isFinished(reader)) return "-";
      if (this.isNoResponse(reader)) return this.$t("tanaoroshiReader.label.progress.NO_RESPONSE");
      return reader.liveness === "DELAYED"
        ? this.$t("tanaoroshiReader.label.progress.DELAYED")
        : this.$t("tanaoroshiReader.label.progress.IN_PROGRESS");
    },

    // ハンディの接続状態は実施中のときだけ意味を持つ。
    // 無応答・終了後の値は取り残された古い値なので出さない。
    handyLabel(reader) {
      if (!this.isInProgress(reader) || reader.handyConnected == null) return "-";
      return reader.handyConnected
        ? this.$t("tanaoroshiReader.label.connected")
        : this.$t("tanaoroshiReader.label.disconnected");
    },

    // リーダー（RFG91）の電池残量。ハンディと同じく実施中のときだけ出す（試験計画書 X-21・U-04）。
    batteryLabel(reader) {
      if (!this.isInProgress(reader) || reader.readerBatteryPct == null) return "-";
      return reader.readerBatteryPct + "%";
    },

    formatDatetime(value) {
      return value ? utils.putSplitWordToCalendarWithTime(value) : "-";
    },

    // 打ち切れるのはまだ作業中のリーダーだけ
    canForceClose(reader) {
      return reader.saveState === "NOT_SAVED";
    },

    openForceCloseDialog(reader) {
      this.forceCloseTarget = reader.htSerial;
      this.forceCloseLabel = reader.deviceNo
        ? this.$t("tanaoroshiReader.label.table.deviceNo") + " " + reader.deviceNo
        : this.$t("tanaoroshiReader.label.table.htSerial") + " " + reader.htSerial;
      this.forceCloseDialog = true;
    },

    closeForceCloseDialog() {
      if (this.isForceClosing) return;
      this.forceCloseDialog = false;
      this.forceCloseTarget = null;
    },

    forceClose() {
      this.isForceClosing = true;
      api
        .post("/tanaoroshi/force-close", {
          tanaoroshiId: this.tanaoroshiId,
          htSerial: this.forceCloseTarget,
        })
        .then((res) => {
          // 打ち切り後の一覧がそのまま返るので、取り直さずに反映する
          if (res.data.status === "0") {
            this.applyResult(res.data.results);
            return;
          }
          this.errorDialog = { message: res.data.errorMessage, errorList: [] };
        })
        .catch((error) => {
          this.errorDialog = { message: utils.getErrorMsg(error.response, this.$t), errorList: [] };
        })
        .finally(() => {
          this.isForceClosing = false;
          this.forceCloseDialog = false;
          this.forceCloseTarget = null;
          // 強制終了できなかったときは、いまの状態（保存済みになった等）を取り直して見せる
          if (this.errorDialog) this.search();
        });
    },

    backToList() {
      this.$router.push({ path: Paths.TANAOROSHI });
    },
  },
};
</script>

<style scoped>
.contents {
  padding: 16px;
}
.back_link {
  display: inline-block;
  font-size: 13px;
  color: #3949ab;
  cursor: pointer;
  margin-bottom: 8px;
}
.back_link:hover {
  text-decoration: underline;
}
.page_title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 12px;
}
.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 12px;
}
.summary_item {
  font-size: 14px;
}
.alert {
  color: #c62828;
}
.alert_row td {
  background-color: #fff5f5;
}
.table_wrap {
  overflow-x: auto;
}
table {
  border-collapse: collapse;
  width: 100%;
}
th {
  background-color: #3949ab;
  color: #fff;
  font-size: 12px;
  padding: 8px 10px;
  white-space: nowrap;
}
td {
  border-bottom: 1px solid #e0e0e0;
  font-size: 12px;
  padding: 8px 10px;
  text-align: center;
  white-space: nowrap;
}
.btn_wrap {
  margin-top: 16px;
}
/* 確認文は改行を入れて読ませる */
.pre_line {
  white-space: pre-line;
}
</style>
